import type { Auth0ContextInterface } from '@auth0/auth0-react';
import type { BAYS, INSURANCE_TYPES } from '../constants';
import type { ReactElement, ReactNode } from 'react';
import type { Moment } from 'moment';
import type {
  EducationSettings,
  ParentCaregiverRoles,
  ParentMaritalStatus,
} from '../components/layout/treatment-plan/background/constants';
import type {
  TreatmentPlanPages,
  TreatmentPlanStatus,
} from '../components/layout/treatment-plan/constants/general';
import type {
  TreatmentPlanGoalStatus,
  GoalTypes,
} from '../components/layout/treatment-plan/goals/old/constants';
import type {
  EFrequency,
  ERecordShared,
  EResponseToTreatment,
} from '../components/layout/treatment-plan/therapy-history/constants';
import type {
  BaselineMeasurementVersion,
  DurationEventMeasurementUnitEnum,
  DurationMethodMCMeasurementModeEnum,
  EventDurationMeasurementUnit,
  MasteryCriteriaChangeTypeEnum,
  RateMethodMeasurementUnitsIntervalUnitEnum,
  TargetStages,
  TPGoalDataCollectionMethod,
  TreatmentPlanResourceVersion,
} from '../components/layout/treatment-plan/goals/common/constants/general';
import type {
  AssessmentNoteStatus,
  ClinicalRelationships,
  FilterAndSortType,
  Roles,
  SupervisionNoteStatus,
  FacilityAssignmentType,
  BTSessionStatus,
  InvoiceStatus,
  InvoicePaymentStatus,
  ActionRequiredItemEnum,
  PayorResponsibilityEnum,
} from '../constants';
import type { DeliveryMethods, Locations } from '../pages/session/btSessionNotes/constants';
import type {
  CQAReviewStatus,
  SessionEvalSchemaVersion,
  Verdict,
} from '../components/layout/qa/constants';
import type {
  ClinicalStatus,
  ProviderClinicalStatus,
  QaBillingStatus,
  RelationshipToPolicyholder,
  SupportedLanguages,
} from '../admin/constants/general';
import type {
  ClaimPaymentStatus,
  PayorTypes,
  RelationshipToPolicyHolder,
  ServiceCodeTypes,
} from '../components/layout/billings/claim-info/enum';
import type {
  ClaimStatusType,
  RecoupmentProbabilityType,
  RecoupmentReasonType,
} from '../components/layout/billings/claim-info/constants';
import type {
  ServiceCodes,
  SessionClaimStatus,
} from '../components/layout/billings/claims/constants';
import type {
  InvoicePaymentStatusEnum,
  InvoicePaymentTypeEnum,
  PaymentTypeEnum,
} from '../components/layout/billings/claim-info/claim-payments/constants';
import type { ClaimSourceEnum, ProviderStatusEnum } from '../pages/constants';
import type { EVVFallbackReason } from '../pages/types';
import type { ToolId } from '../components/layout/treatment-plan/initialAssessment/constants/types/general';
import type { DiagnosisReviewed } from '../components/layout/treatment-plan/initialAssessment/constants/types/general';

export interface ISignsAndSymptomsObserved {
  other: null | string;
  play_skills_deficit: boolean;
  motor_skills_deficit: boolean;
  maladaptive_behaviors: boolean;
  adaptive_skills_deficit: boolean;
  cognitive_skills_deficit: boolean;
  social_interaction_deficit: boolean;
  social_communication_deficit: boolean;
  joint_attention_skills_deficit: boolean;
  community_safety_skills_deficit: boolean;
  restricted_repetitive_behaviors: null | boolean;
  restricted_receptive_behaviors: null | boolean;
  school_readiness_skills_deficit: boolean;
  nonverbal_communication_deficit: boolean;
  social_emotional_reciprocity_deficit: boolean;
  expressive_receptive_communication_deficit: boolean;
}

export interface IEvidenceBasedInterventionsUsed {
  other: null | string;
  modeling: boolean;
  prompting: boolean;
  reinforcement: boolean;
  discrete_trial_teaching: boolean;
  naturalistic_intervention: boolean;
  pivotal_response_training: boolean;
  functional_behavior_assessment: boolean | null;
  antecedent_based_interventions: boolean;
  parent_implemented_intervention: boolean;
  cognitive_behavioral_intervention: boolean;
  functional_communication_training: boolean;
  picture_exchange_communication_systems: boolean;
  peer_mediated_instruction_and_intervention: boolean;
  differential_reinforcement_of_alternative_incompatible_or_other_behaviors: boolean;
}

export interface IAwaitingAcknowledgement {
  id: number;
  session_provider_id: number;
}

export interface INarrativeSuggestionResponse {
  narrative: string;
}

export type NarrativeSuggestionNoteType = '97155' | '97156';

export interface NarrativeSuggestionContext {
  noteId: number;
  noteType: NarrativeSuggestionNoteType;
  sourceKey: string;
  contextToken?: string;
}

export interface IBTSession {
  id?: string;
  client_id: number;
  date_start: Moment;
  date_end: Moment;
  utc_offset_date_start: number;
  utc_offset_date_end: number;
  status: BTSessionStatus;
  expected_duration: number;
  location: Locations;
  delivery_method: DeliveryMethods;
  claim_history: IClaimStatusHistory[];
  date_reviewed: Moment;
  date_provider_signed: string;
  utc_offset_date_reviewed: number;
  review_comments: string;
  signs_and_symptoms_observed: ISignsAndSymptomsObserved;
  evidence_based_interventions_used: IEvidenceBasedInterventionsUsed;
  client_progress: string;
  clinical_relationship: ClinicalRelationships;
  synopsis: {
    client_adheres_with_targets_or_goals: boolean;
    no_behavior_issues_or_family_changes: boolean;
  };
  narrative: string;
  is_successful: boolean;
  is_supervised: boolean;
  service_code_id?: number;
  note_version?: 'v1' | 'v2';
  breaks?: ISessionBreak[];
  treatment_plan_id?: number | null;
  active_treatment_plan_id?: number;
  client?: IClient;
  goals_and_trials: ISessionGoalsAndTrials | any[];
  active_goals?: ITreatmentPlanGoal[];
  session_provider?: ISessionProvider;
  clinical_qa_review?: IClinicalQAReview;
  claim_id?: number;
  last_ai_generated_session_narrative?: string;
  provider_signature?: string;
  guardian_provider_signature?: string;
  bay?: string | null;
  evv_required?: boolean;
  evv_start_captured?: boolean;
  evv_end_captured?: boolean;
}

export type EVVTokenKind = 'start' | 'end';

export type EVVTokenStatus = 'active' | 'captured' | 'expired';

export interface IEVVTokenCreateResponse {
  token: string;
}

export interface IEVVSideStatus {
  captured?: boolean;
  captured_at?: string | null;
  lat?: number | null;
  long?: number | null;
  accuracy_meters?: number | null;
  token_status?: EVVTokenStatus | null;
  token_expires_at?: string | null;
  fallback_reason: string | null;
}

export interface IEVVSessionStatus {
  start: IEVVSideStatus;
  end: IEVVSideStatus;
}

export interface IEVVCaptureGpsPayload {
  lat: number;
  long: number;
  accuracy_meters?: number;
  device_info?: string;
}

export interface IEVVCaptureDesktopPayload extends IEVVCaptureGpsPayload {
  reason: EVVFallbackReason;
}

export interface IEVVCaptureGpsResponse {
  status: 'captured';
}

export interface ISessionBreak {
  id?: number;
  start_offset_seconds: number;
  end_offset_seconds: number;
  duration_seconds?: number | null;
}

export interface ISessionGoalsAndTrials {
  [key: number]: ISessionGoalTrailsData;
}

export enum SessionGoalTimingType {
  Manual = 'manual',
  Auto = 'auto',
}

export interface ISessionGoalTiming {
  id?: number;
  timing_type: SessionGoalTimingType;
  start_offset_seconds: number | null;
  end_offset_seconds: number | null;
  duration_seconds?: number | null;
  order_index?: number;
}

export interface ISessionGoalTrailsData {
  goal_name: string;
  goal_type?: GoalTypes;
  data_collection_method: TPGoalDataCollectionMethod | null;
  linked_goals: {
    behavior_reduction?: ISessionLinkedGoal[];
    skill_acquisition?: ISessionLinkedGoal[];
  };
  techniques?: string[];
  timings?: ISessionGoalTiming[];
  trials: any[];
}

interface ISessionLinkedGoal {
  goal_name: IBehaviorGoalName;
  id: number;
}

export interface ISessionPracticedTargetTrial {
  id?: number;
  target_id?: number;
  date_measured?: Moment;
  utc_offset?: number;
  expected_response: number;
  occurrence: number;
  target: ITreatmentPlanGoalTarget;
  attempts: number;
  instances?: ITargetTrialInstanceDuration[];
  rate_events?: ITargetTrialRateEvent[];
  duration_events?: ITargetTrialDurationEvent[];
}

export interface ISessionMinified {
  id?: string;
  client_id: number;
  date_start: Moment | string;
  date_end?: Moment | string;
  utc_offset_date_start: number;
  utc_offset_date_end: number;
  location?: string;
  status: BTSessionStatus;
  expected_duration: number;
  client?: IClient;
  bcba?: ISessionProviderMinified;
  session_provider?: ISessionProviderMinified;
  clinical_qa_review?: IClinicalQAReviewMinified;
  bay?: string | null;
  clinical_relationship: ClinicalRelationships;
  date_provider_signed: string | null;
}

export interface ISessionMinifiedWithClaimHistory extends ISessionMinified {
  claim_history: IClaimStatusHistory[];
}

interface ISessionProviderMinified {
  email: string;
  first_name: string;
  last_name: string;
  id?: number;
}

export interface SupervisionHistoryProps {
  clientSupervisions: IClientSupervision[];
  btSupervisions: IBtSupervision[];
  parentSupervisions: IParentSupervision[];
  groupSupervisions: IGroupSupervision[];
  loading: boolean;
}

export interface IMedication {
  id?: number;
  medication_name?: string;
  dosage?: string;
  physician?: string;
  reason?: string;
  date_start?: string;
  utc_offset?: number;
  treatment_plan_id?: number;
  range?: number;
}

interface IAdditionalFilesReviewed {
  is_previous_assessment_reports_reviewed: boolean;
  is_previous_intervention_plans_and_treatment_plans_reviewed: boolean;
  is_reports_by_other_providers_reviewed: boolean;
  is_school_reports_reviewed: boolean;
  is_medical_reports_reviewed: boolean;
}

export interface IInitialAssessment extends IAdditionalFilesReviewed, IAssessmentSummary {
  date_of_assessment: Moment | null;
  date_of_reassessment: Moment | null;
  clinical_interview_or_summary: string;
  assessment_result?: string;
  is_diagnostic_reports_reviewed: DiagnosisReviewed | boolean;
  id?: number;
}

export interface IMaladaptiveBehavior {
  label: string;
  associated_dsm5_symptom: string;
  maladaptive_concerns?: string | null;
  id?: number;
}

export interface IAssessmentTool {
  tool_name?: ToolId | '';
  date_tool_used?: string | null;
  reason_for_use_age_group?: string;
  reason_for_use_symptoms?: string;
  score?: string;
  description?: string;
  id?: number;
  visuals?: IAssessmentVisual[];
}

export interface IAssessmentSummary {
  summary_observations_during_assessment?: string;
  summary_assessment_result?: string;
  summary_priorities_based_on_assessment?: string;
}

export interface IAssessmentVisual {
  description: string;
  label: string;
  document_url?: string;
  document_contents?: any;
  noPreviewFile?: boolean;
  fileName?: string;
  file_type?: string;
  id?: number;
  tool_id?: number;
}

export interface IGoalsPracticed {
  id?: number;
  behavior: string;
  goal: string | undefined | null;
  settings: 'Home' | 'Community' | undefined;
  success: number;
  attempts: number;
}

export interface ITreatmentPlanStatus {
  id: number;
  label: string;
}

export interface IProvider {
  id: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  sex: string;
  dob: string;
  email: string;
  clinical_status?: ProviderClinicalStatus;
  clinical_status_reason?: string;
  street_address_line_one: string;
  street_address_line_two: string;
  street_address_zip: number;
  street_address_city: string;
  street_address_state: string;
  phone: string;
  npi: string;
  certification: string;
  date_certification_start?: string;
  date_certification_end?: string;
  state_license: string;
  provider_type: string;
  employment_type?: string | null;
  job_title?: string | null;
  caqh_id?: string;
  payroll_id?: string;
  roles?: Roles[];
  auth_provider_create?: boolean;
  qa_billing_status?: QaBillingStatus;
  evv_required?: boolean;
  is_reviewer_bcba?: boolean;
  supported_languages?: SupportedLanguages[];
  preferred_language?: SupportedLanguages;
  status?: ProviderStatusEnum;
}

export interface IGuardianRelationshipManagerAssignment {
  id: number;
  date_start: string;
  date_end?: string | null;
  guardian_provider: IProvider;
  relationship_manager_provider: IProvider;
}

export type GetProvidersByRolePayload = {
  role: Roles;
  getAccessTokenSilently: GetAccessTokenSilently;
};

export type GetProviderByIdPayload = {
  providerId: number | string;
  getAccessTokenSilently: GetAccessTokenSilently;
};

type ProviderUpdateValue = string | number | boolean | null | undefined | Date | string[];

export type UpdateProviderByIdPayload = GetProviderByIdPayload & {
  data: Record<string, ProviderUpdateValue>;
};

interface ISessionProvider extends IProvider {
  session_id: number;
  service_code_id: number;
}

// The relationship_manager endpoint returns a trimmed provider on both sides of the assignment.
export type IRelationshipManagerProvider = Pick<
  IProvider,
  'id' | 'first_name' | 'last_name' | 'email' | 'status' | 'clinical_status'
>;

// Only the relationship manager side carries contact details — the guardian side stays trimmed.
// `time_zone` is an IANA zone id the API derives from the manager's address, and is null when
// that address has no usable zip.
export interface IRelationshipManagerContactProvider extends IRelationshipManagerProvider {
  phone: string | null;
  time_zone: string | null;
}

export interface IRelationshipManagerAssignment {
  id: number;
  date_start: string;
  date_end: string | null;
  guardian_provider: IRelationshipManagerProvider;
  relationship_manager_provider: IRelationshipManagerContactProvider;
}

export interface IGetRelationshipManagersArg {
  guardianProviderId: number;
  getAccessTokenSilently: GetAccessTokenSilently;
}

export interface IClient {
  id?: number;
  first_name: string;
  last_name: string;
  middle_name: string;
  sex: string;
  dob: Date | string;
  email: string | null;
  clinical_status?: ClinicalStatus;
  clinical_status_reason?: string;
  street_address_line_one: string;
  street_address_line_two: string;
  street_address_zip: number | string;
  street_address_city: string;
  street_address_state: string;
  phone: string;
  providers?: IProvider[];
  active_treatment_plan_id?: number | null;
  evv_required?: boolean;
}

export interface IFacility {
  id?: number;
  label: string;
  tin: string;
  npi: string;
  street_address_line_one: string;
  street_address_line_two: string;
  street_address_zip: string;
  street_address_city: string;
  street_address_state: string;
}

export interface IPayor {
  id?: number;
  label: string;
  state: string;
  clearinghouse_payor_id: string;
  type: string;
  modifier: string;
  preferred_service_code_type: string;
  is_using_taxonomy_code: boolean;
  taxonomy_code?: string;
  contracts: IPayorContract[];
  service_code_descriptions: IServiceCodeDescription[];
}

export interface IServiceCodeDescription {
  id: number;
  service_code_id: number;
  service_code?: string;
  payor_id: number;
  description: string;
}

export interface IOption {
  id?: number | string;
  label: string;
  description?: string;
}
export interface ITimesheet {
  id?: number;
  client_id: number;
  client_name?: {
    first_name: string;
    last_name: string;
  };
  service_code_id: number;
  service_code?: {
    code: string;
    id: number;
    is_billable: boolean;
    is_timesheet_usable: boolean;
    label: string;
  };
  date_start: Moment;
  date_end: Moment;
  label: string;
  utc_offset: number;
  provider_id?: number;
  provider_name?: {
    first_name: string;
    last_name: string;
  };
}

// ITimesheetTransformedData has approximately similar properties as ITimesheet
// To allow sorting and filtering to work properly in mui-datatables Timesheet data
// is transformed accordingly, as part of it few of the objects are converted to string type
export interface ITimesheetTransformedData {
  id?: number;
  client_id: number | string | null;
  client_name?: string;
  service_code_id: number | string;
  service_code?: string;
  date_start: Moment | string;
  date_end: Moment | string;
  label: string;
  utc_offset: number;
  provider_id?: number;
  provider_name?: string;
  read_only?: boolean;
  pay_period?: string;
}

export interface IFilterOutput {
  [name: string]: Array<{
    id: number | string;
    label: string;
    disabled?: boolean;
    selected?: boolean;
  }>;
}

export interface IFilterChip {
  id: number | string;
  label: string;
  filterName: string;
  disableDelete?: boolean;
}

export interface ITherapyUtilization {
  client_id: number;
  hours_allowed: number;
  hours_completed: number;
  service_code: string;
  utc_offset: number;
  utilization: number;
  week_ends_on: string;
  week_starts_on: string;
}

export interface IFilterAndSortItem {
  label?: string;
  secondaryLabel?: string;
  type: FilterAndSortType;
  order: number;
  disabled?: boolean;
  options: IFilterAndSortOption[];
  dataTestid?: string;
  LabelIcon?: (props: any) => ReactElement;
}

export interface IFilterAndSortOption {
  id: number | string;
  label: string;
  disabled?: boolean;
  selected?: boolean;
  dataTestId?: string;
}

export interface ITPPriorAuthorizations {
  id: number;
  payor_responsibility?: PayorResponsibilityEnum;
  payor_id?: number;
  client: {
    first_name: string;
    last_name: string;
    sex: string;
    dob: string;
    clinical_status: ClinicalStatus;
    id: number;
  };
  date_end: string;
  days_until_expiry: number;
  active_treatment_plan_id: number;
}

export interface IServiceCode {
  id?: number;
  code: string;
  is_billable: boolean;
  is_timesheet_usable: boolean;
  label: string;
  type?: ServiceCodeTypes;
  linked_code?: string;
}

export interface IServicesTransformed {
  [key: string]: IRecommendationService;
}

export interface IClientFacilityMap {
  id?: number;
  assignment_type: FacilityAssignmentType | null;
  client: IClient;
  facility: IFacility;
  date_start: Date;
  date_end: Date;
}

export interface IProviderLicensesMapping {
  [providerId: string]: IProviderLicenseDetails;
}

export interface IProviderPayorCredentialsMapping {
  [providerId: string]: IProviderPayorCredentialDetails;
}

export interface IProviderLicenseDetails {
  state: string;
  is_license_required_in_state: boolean;
  licenses: IProviderLicense[];
}

export interface IProviderLicense {
  id: number;
  provider_id: number;
  status: string;
  date_start: string;
  date_end: string | null;
  tasks: string[];
  priority: string | null;
}

export interface IProviderPayorCredentialDetails {
  payor_sector: string | null;
  state: string;
  is_medicaid_credential_required: boolean;
  is_no_medicaid_state: boolean;
  credentials: IProviderPayorCredential[];
  medicaid_credentials: IProviderPayorCredential[];
}

export interface IProviderPayorCredential {
  id: number;
  provider_id: number;
  payor_id: number;
  date_start: string;
  date_end: string | null;
  medicaid_id: string | null;
}

export interface IClientPolicyMap {
  client_id: number;
  date_end?: Moment | null;
  date_start: Moment;
  id: number;
  member_id: string;
  payor_responsibility: string;
  is_monthly_plan?: boolean | null;
  payor: {
    label: string;
    state: string;
    clearinghouse_payor_id?: string;
    changehealthcare_eligibility_payor_id?: string;
    type: string;
    modifier?: string;
    sector?: string;
    preferred_service_code_type?: string;
    is_using_taxonomy_code?: string;
    id: number;
    taxonomy_code?: string;
    service_code_descriptions?: IServiceCodeDescription[];
  };
  billing_payor: {
    label: string;
    state: string;
    clearinghouse_payor_id?: string;
    changehealthcare_eligibility_payor_id?: string;
    type: string;
    modifier?: string;
    sector?: string;
    preferred_service_code_type?: string;
    is_using_taxonomy_code?: string;
    id?: number;
    taxonomy_code?: string;
  };
  policyholder?: {
    street_address_line_one: string;
    street_address_line_two: string;
    street_address_city: string;
    street_address_state: string;
    street_address_zip: string;
    name: string;
    sex: string;
    dob: Moment;
    phone: string;
  };
  relationship_to_policyholder?: RelationshipToPolicyholder;
  group_number?: string;
  is_PA1_required?: boolean;
  is_PA2_required?: boolean;
}

export interface IClientAuthorization {
  date_start: string;
  date_end: string;
  period: string;
  service_code_id: number;
  is_active: boolean;
  hours_allowed: number;
  is_overage_allowed: boolean;
  external_reference: string;
  id?: number;
}

export interface IPayPeriods {
  present: Array<string>;
  past: Array<Array<string>>;
  future: Array<Array<string>>;
  past_payperiod_data_input_end_date?: string;
}

export interface IParentGoalProgress {
  goal_id: number;
  goal_evaluation: string;
  id?: number;
}

export interface IClientFamily {
  client: {
    first_name: string;
    last_name: string;
    clinical_status: ClinicalStatus;
    sex: string;
    dob: string;
    id: number;
  };
  parents_and_caregivers: {
    name: string;
    id: number;
    email: string | null;
    phone: string | null;
  }[];
}

export interface ITrainingTopic {
  id: number;
  group_parent_supervision_id: number;
  description: string;
  label: string;
}

export interface IGroupSupervision {
  date_start: string;
  utc_offset_date_start: number;
  duration: number;
  setting: string;
  id?: number;
  clients: {
    first_name: string;
    last_name: string;
    clinical_status: boolean;
    sex: string;
    dob: string;
    id: number;
  }[];
  bcba: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  training_topics: ITrainingTopic[];
  family_attendance: IFamilyAttendance[];
  status: SupervisionNoteStatus;
}

export interface IFamilyAttendanceTopic {
  id: number;
  family_attendance_id: number;
  label: string;
  training_topic_id: number;
}

export interface IFamilyAttendance {
  id: number;
  group_parent_supervision_id: number;
  date_start: string;
  bay?: string | null;
  date_end: string;
  duration: number;
  client: {
    first_name: string;
    last_name: string;
    clinical_status: boolean;
    sex: string;
    dob: string;
    id: number;
  };
  parent_and_caregiver_one: {
    role: string;
    name: string;
    id: number;
  };
  parent_and_caregiver_two: {
    role: string;
    name: string;
    id: number;
  };
  topics: IFamilyAttendanceTopic[];
  location: string;
  response: string;
}

export interface TargetProgress {
  goal_label: string;
  data_collection_method: string;
  target_evaluations: {
    id?: number;
    target_id: number;
    target_label: string;
    successful_attempts: number;
    total_attempts: number;
    unsuccessful_attempts: number;
    occurrence: number;
    rate_events: {
      id?: number;
      date_of_event: string;
    }[];
    duration_events: ITargetTrialDurationEvent[];
  }[];
}

export interface IParentSupervision {
  bay?: string | null;
  client: IClient;
  bcba: ISessionProviderMinified;
  id?: number;
  date_start: Moment;
  date_signed?: Moment;
  utc_offset_date_start?: number;
  utc_offset_date_signed?: number;
  location: string;
  setting: string;
  version: 'v1' | 'v2';
  target_progress: TargetProgress[];
  expected_duration?: number;
  parent_progress?: string;
  goals_and_targets_adhered: boolean;
  client_id: number;
  session_narrative?: string;
  last_ai_generated_session_narrative?: string;
  signature?: string;
  parent_one_parent_and_caregiver_id: number | null;
  parent_two_parent_and_caregiver_id: number | null;
  goal_progress?: Array<IParentGoalProgress>;
  status: SupervisionNoteStatus;
  active_treatment_plan_id: number;
  parent_one_parent_and_caregiver?: IParentCaregiver;
  parent_two_parent_and_caregiver?: IParentCaregiver;
}

export interface IBtSupervision {
  id?: number;
  client_id: null | number;
  bt_provider?: ISessionProviderMinified;
  bt_provider_id: null | number;
  utc_offset_date_start: number;
  date_start: Moment | null;
  duration: number | '';
  location: string;
  setting: string;
  date_signed: Moment;
  utc_offset_date_signed: number;
  has_materials_organized_prior_to_session: string;
  builds_rapport_with_learner: string;
  implements_programs_and_collects_data_regularly: string;
  provides_clear_sds_and_keeps_appropriate_pace: string;
  varies_reinforces_assesses_as_needed: string;
  provides_praise_for_independent_responses: string;
  uses_appropriate_ratio_of_mastered_novel_tasks: string;
  uses_prompts_appropriately: string;
  implements_correction_procedures_appropriately: string;
  uses_more_positive_than_corrective_statements: string;
  uses_non_verbal_prompts_when_possible: string;
  remains_calm_during_challenging_behaviors: string;
  follows_behavior_protocol_and_records_data_as_outlined: string;
  professionalism: string;
  collaborates_effectively_with_colleagues: string;
  seeks_feedback_for_growth_in_clinical_skills: string;
  additional_comments: string;
  signature: null | string;
  status: SupervisionNoteStatus;
  client?: IClient;
  bcba?: IProvider;
}

export interface SupervisionMetricsItem {
  client: {
    clinical_status: string;
    dob: string;
    first_name: string;
    id: number;
    last_name: string;
    sex: string;
  };
  client_supervision_duration: number;
  csn_session_ratio: number;
  additional_csn_duration_required: number;
  parent_supervision_duration: number;
  session_duration: number;
  group_parent_supervision_duration: number;
  client_assessment_duration: number;
  threshold: number | null;
}

export interface IActionRequiredItem {
  id: number;
  type: ActionRequiredItemEnum;
  item:
    | IBtSupervision
    | IClientSupervision
    | IGroupSupervision
    | IParentSupervision
    | ITreatmentPlan
    | IClientAssessment;
  date_start: Moment | Date | string | null;
  status?: string;
  duration?: number;
}

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export interface ISelectOption {
  value: SelectValueType;
  label: string | number | ReactNode;
  disabled?: boolean;
}

export type SelectValueType = string | number;

export interface ITableHeaderConfig {
  sx: any;
  label: string;
  showRightBorder?: boolean;
  labelAlign?: 'left' | 'right' | 'center';
  labelWeight?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  labelColor?: string;
}

export interface IActivationLog {
  date_changed: string;
  id: number;
  new_value: boolean;
  previous_value: boolean;
}

export interface ITreatmentPlan {
  id?: number;
  client_id?: number;
  activation_logs?: IActivationLog[];
  version?: number;
  date_start_authorization_recommended?: Moment | string;
  date_end_authorization_recommended?: Moment | string;
  is_active?: boolean;
  status_id?: number;
  provider_id?: number;
  provider?: ISessionProviderMinified;
  date_treatment_plan_started?: Moment | string;
  date_treatment_plan_ended?: Moment | string;
  utc_offset_date_end?: number;
  signature?: string;
  has_medication?: boolean;
  bay?: Bays;
  client?: IClient;
  status?: {
    date: string;
    label: TreatmentPlanStatus;
  };
  resource_versions?: {
    goal: TreatmentPlanResourceVersion;
    target: TreatmentPlanResourceVersion;
    medical_history: TreatmentPlanResourceVersion;
    client_and_family_background: TreatmentPlanResourceVersion;
    assessment: TreatmentPlanResourceVersion;
  };
  prior_authorization_number_2?: string;
  client_and_family_background?: IClientFamilyBackground;
  medical_history?: IMedicalHistory;
  treatment_recommendation?: IRecommendation;
}

export interface ITreatmentPlanLinkedGoal {
  id: number;
  goal_name?: IBehaviorGoalName;
}

interface ITreatmentPlanLinkedGoals {
  skill_acquisition?: ITreatmentPlanLinkedGoal[];
  behavior_reduction?: ITreatmentPlanLinkedGoal[];
}

export interface IDDClientGoals {
  id: number;
  label: string;
  goal_type: GoalTypes;
  targets: ITreatmentPlanGoalTarget[];
  treatment_plan_id?: number;
  mastery_criteria?: ITreatmentPlanGoal['mastery_criteria'];
}

export interface IDataDashboardTargetOption {
  id: string;
  label: string;
  target_ids: number[];
}

export interface IDataDashboardGoalOption {
  id: string;
  goal_name_id: number;
  label: string;
  goal_type: GoalTypes;
  data_collection_method: TPGoalDataCollectionMethod;
  measurement_units: ITreatmentPlanGoal['measurement_units'];
  goal_ids: number[];
  targets: IDataDashboardTargetOption[];
}

export interface IDataDashboardOptionsParams {
  client_id: number;
  start_date: string;
  end_date: string;
  treatment_plan_id?: number;
}

export interface IDataDashboardOptionsRequest {
  params: IDataDashboardOptionsParams;
  getAccessTokenSilently: GetAccessTokenSilently;
}

export interface IDataDashboardOptionSelection {
  goalId: string | null;
  targetIds: string[];
}

export interface IHypothesizedFunction {
  id?: number;
  date_selected: string;
  is_attention: boolean;
  is_escape_or_avoidance: boolean;
  is_sensory: boolean;
  is_tangible: boolean;
  date_edits_blocked: string | null;
}

export interface IInstanceMasteryCriteriaItem {
  change_type: MasteryCriteriaChangeTypeEnum | null;
  desired_amount: number | null;
  practice_duration_count: number | null;
  practice_duration_unit: string | null;
}

export interface ICommonRateMasteryCriteriaItem {
  change_type: MasteryCriteriaChangeTypeEnum | null;
  desired_amount: number | null;
  practice_duration_count: number | null;
  total_attempts: number | null;
}

export interface ICommonDurationMasteryCriteriaItem {
  change_type: MasteryCriteriaChangeTypeEnum;
  measurement_mode: DurationMethodMCMeasurementModeEnum;
  desired_amount: number | null;
  practice_duration_count: number | null;
}

export interface ISAGInstanceMasteryCriteriaItem {
  acquisition: IInstanceMasteryCriteriaItem;
  generalization: IInstanceMasteryCriteriaItem;
  maintenance: IInstanceMasteryCriteriaItem;
  maintenance_frequency: string | null;
}

export interface ISAGDurationMasteryCriteriaItem {
  acquisition: ICommonDurationMasteryCriteriaItem;
  generalization: ICommonDurationMasteryCriteriaItem;
  maintenance: ICommonDurationMasteryCriteriaItem;
  maintenance_frequency: string | null;
}

export interface ISAGRateMasteryCriteriaItem {
  acquisition: ICommonRateMasteryCriteriaItem;
  generalization: ICommonRateMasteryCriteriaItem;
  maintenance: ICommonRateMasteryCriteriaItem;
  maintenance_frequency: string | null;
}

export interface IParencetageMasteryCriteriaItem {
  desired_amount: number | null;
  practice_duration_count: number | null;
  total_attempts: number | null;
}

export interface ISAGMasteryCriteria {
  acquisition: IParencetageMasteryCriteriaItem;
  generalization: IParencetageMasteryCriteriaItem;
  maintenance: IParencetageMasteryCriteriaItem;
  maintenance_frequency: string | null;
}

export type IBRGMasterCriteria = IInstanceMasteryCriteriaItem;

export type IPCGMasteryCriteria = IParencetageMasteryCriteriaItem;

export interface ICommonMeasurementUnits {
  total_attempts: number;
}

export interface IRateMeasurementUnits {
  interval_size: number;
  interval_unit: RateMethodMeasurementUnitsIntervalUnitEnum;
}

export interface IDurationMeasurementUnits {
  duration_unit: DurationEventMeasurementUnitEnum;
}

export interface ITPResouceVersion {
  baseline_measurement: BaselineMeasurementVersion;
}

export interface ITreatmentPlanGoal {
  id?: number;
  goal_name_id?: number | string;
  status_at_creation?: TreatmentPlanGoalStatus | null;
  date_introduced: string;
  comparand_type?: string;
  data_collection_method: TPGoalDataCollectionMethod | null;
  measurement_units:
    | ICommonMeasurementUnits
    | IRateMeasurementUnits
    | IDurationMeasurementUnits
    | null;
  baseline_at_creation: number | string | null;
  current_level_at_creation?: number | string | null;
  description?: string;
  operational_definition?: string;
  objective?: string;
  antecedent_modifications?: string;
  consequence_modifications?: string;
  hypothesized_functions?: IHypothesizedFunction[];
  target_mastery?: number;
  goal_type: GoalTypes;
  mastery_criteria:
    | IBRGMasterCriteria
    | IPCGMasteryCriteria
    | ISAGMasteryCriteria
    | null
    | ISAGRateMasteryCriteriaItem
    | ICommonRateMasteryCriteriaItem
    | ICommonDurationMasteryCriteriaItem
    | ISAGDurationMasteryCriteriaItem
    | ISAGInstanceMasteryCriteriaItem;
  resource_versions: ITPResouceVersion;
  is_maladaptive_correction: boolean;
  maladaptive_symptom_id?: number;
  linked_skill_acquisition_goal_ids?: Array<number> | null;
  is_active: boolean;
  is_linkable?: boolean;
  goal_name?: IBehaviorGoalName;
  progress?: number;
  targets: ITreatmentPlanGoalTarget[];
  reason?: string;
  linked_goals?: ITreatmentPlanLinkedGoals;
  expected_mastery_date?: string | null;

  // The following fields are only used in FE side due to the validation of "Save Goal" button, editing, expanding.
  dirty?: boolean;
  is_editing?: boolean;
  expanded?: boolean;
  treatment_plan_id?: number;
  goal_version?: string;
}

export interface ITreatmentPlanGoalTarget {
  id?: number;
  label: string;
  order: number;
  description?: string;
  instruction: string;
  is_event_duration_required: boolean;
  event_duration_measurement_unit: EventDurationMeasurementUnit | null;
  is_active: boolean;
  is_goal_active?: boolean;
  is_goal_linkable?: boolean;
  reason?: string;
  goal?: {
    id: number;
    is_active: boolean;
    goal_name: IBehaviorGoalName;
  };
  stage?: TargetStages | null;
  stage_change_logs?: IStageChangeLog[];
  last_activation_log?: ITargetActivationLog | null;
  maintenance_frequency?: string | null;
  // The following fields are only used in FE side due to the validation of "Save Target" button and editing.
  expanded?: boolean;
  is_editing?: boolean;
  dirty?: boolean;
  client_progress_expanded?: boolean;
}

export interface ITargetActivationLog {
  date: string;
  is_active: boolean;
  reason: string;
}

export interface IBehavior {
  id?: number;
  label: string;
}

//Interface for Target Stage Change request
export interface ITargetStageChange {
  goalId?: number;
  target_id: number;
  date_stage_changed: string;
  stage: TargetStages;
  reason_for_change: string | null;
}

//Interface for Target Stage Change response
export interface IStageChangeLog {
  id: number;
  old_stage: TargetStages | null;
  new_stage: TargetStages;
  date_stage_changed: string;
  reason_for_change: string;
  order: number;
  date_edits_blocked: string | null;
}
export interface IBehaviorGoalName {
  id?: number;
  behavior?: IBehavior;
  label: string;
  prefix?: string;
  is_active?: boolean;
}

export interface IReferralAssessmentTool {
  tool_name: string;
  id?: number;
}

export interface ICrisisPlan {
  id?: number;
  is_needed: boolean;
  crisis_description: string;
  precursor: string;
  consequence_strategy: string;
  antecedent_intervention: string;
  behavior_intervention_plan: string;
  follow_up_step: string;
}

export interface ITreatmentPlanSymptom {
  id?: number;
  label: string;
  associated_dsm5_symptom: string;
}

export type ITreatmentPlanBages = {
  [key in TreatmentPlanPages]: number;
};

export type ITreatmentPlanStatusIdByLabel = {
  [key in TreatmentPlanStatus]: number | undefined;
};

export interface IDischargeCriteria {
  mastery_of_goals: string;
  readiness_to_move_on_from_current_service: string;
  step_down_in_program_hours: string;
  communication_amongst_providers: string;
  id?: number;
}

export interface IDischargeTransitionReason {
  id?: number;
  label: string;
  description: string | null;
}

export interface IDiagnosis {
  diagnosis_name: string;
  severity_level?: string;
  provider_name: string;
  year_diagnosis_made: number | string;
  city_diagnosis_made: string;
  state_diagnosis_made: string;
  id?: number;
}

export interface IDiagnosisIncomplete {
  diagnosis_name?: string;
  severity_level?: string;
  provider_name?: string;
  year_diagnosis_made?: number | string;
  city_diagnosis_made?: string;
  state_diagnosis_made?: string;
  id?: number;
}

export interface IMedicalHistory {
  allergies: string;
  special_diets: string;
  autism_severity_level: string;
  provider_name: string;
  year_autism_diagnosis_made: number | string;
  city_autism_diagnosis_made: string;
  state_autism_diagnosis_made: string;
  id?: number;
  diagnoses?: Array<IDiagnosis>;
  trauma_history: string;
}

export interface IReferralHistory {
  referring_provider_name: string;
  reason_for_referral: string;
  id?: number;
  assessment_tools?: Array<IReferralAssessmentTool> | null;
  date_of_diagnosis: string | null;
}
export interface IRecommendationService {
  service_code: IServiceCode | string;
  hours: number;
  frequency: string;
  location: string;
  id?: number;
}

export interface IUnsavedRecommendationService extends IRecommendationService {
  tpId?: number;
  recommendationId?: number;
}

export interface IRecommendation {
  id?: number;
  considered_factors: string;
  provision_description: string;
  explanation: string;
  date_start: string;
  duration: number;
  services?: IRecommendationService[];
}

export interface IEducationBackground {
  id?: number;
  is_iep: boolean;
  is_504: boolean;
  education_setting: EducationSettings;
  education_description: string;
  focus_area_description: string;
  hours_per_week: string;
  education_support: string;
  education_evaluation: string;
}

export interface IClientFamilyBackground {
  id?: number;
  other_individuals_in_home: string;
  member_language: string;
  parents_and_caregivers: Array<IParentCaregiver>;
  siblings: Array<ISibling>;
  family_medical_history_maternal?: string;
  family_medical_history_paternal?: string;
  community_involvement_religious_affiliations?: string;
}

export interface IParentCaregiver {
  id: number;
  role: ParentCaregiverRoles;
  name: string;
  parent_marital_status?: ParentMaritalStatus;
  parent_mental_health_history?: string;
  email?: string;
  phone?: string;
  job?: string;
  street_address_line_one?: string;
  street_address_line_two?: string;
  street_address_city?: string;
  street_address_state?: string;
  street_address_zip?: string;
}

export interface ISibling {
  id?: number;
  name: string;
  age: number;
  diagnosis: string;
}

export interface IAbaTherapyService {
  provider_name: string;
  service_type: string;
  hours: number;
  frequency: EFrequency;
  response_to_treatment: EResponseToTreatment;
  is_record_shared: ERecordShared | null;
  date_start: string;
  date_end: string | null;
  is_parents_authorization_provided: boolean;
  id?: number;
}
export interface IAbaTherapy {
  has_service_history: boolean;
  id?: number;
  services?: IAbaTherapyService[];
}

export interface ITargetTrialInstanceDuration {
  id: number;
  duration: number;
}

export interface ITargetTrialRateEvent {
  id?: number;
  date_of_event?: Date;
}

export interface ITargetTrialDurationEvent {
  id?: number;
  duration?: number;
}

export interface IDashboardTarget {
  id: number;
  label: string;
  type: string;
  timeSeriesData: IDashboardTargetDay[];
  event_duration_measurement_unit?: string;
}

export interface IPriorAuthorizationDetail {
  prior_authorization_request_id: number | null;
  client_id: number | null;
  policy_id: number | null;
  prior_authorization_number_issued: string | null;
  authorization_type: string | null;
  date_of_service_start_approved: string | null;
  date_of_service_end_approved: string | null;
  status: string | null;
  submission_bay: string | null;
  service_code_id: number | null;
  approved_units: number | null;
  units_consumed: number | null;
  provider_modifier: string | null;
  service_modifier: string | null;
  max_units_per_claim: number | null;
}
export interface ITrialSummary {
  goal_id: number;
  baseline_at_creation: number;
  date_introduced: string;
  label: string;
  targets: ITrialSummaryTarget[];
}

export interface ITrialSummaryTarget {
  label: string;
  phase_changes: ITargetPhaseChange[];
  target_id: number;
  trials: IDashboardTargetDay[];
  event_duration_measurement_unit: string | null;
}

export interface ITargetPhaseChange {
  id: number;
  title: string;
  date_added: string;
  date_timestamp?: number;
  target_id?: number;
  line_label?: number | string;
  type?: string;
}

export interface ITrialAverageTableData {
  target: string;
  label: string;
  value: string;
  unit: string;
}

export interface IDurationTrialAverageTableData {
  target: string;
  label: string;
  average: string;
  total: string;
}

export interface IDashboardTargetDay {
  session_id?: number;
  date: string;
  attempts: number;
  success: number;
  instances: number;
  percentage: number;
  type: string;
  target_label: string;
  rate?: number;
  durations?: number[];
  total_duration?: number;
  duration_events?: number[];
  durationEventTotal?: number;
  durationEventAverage?: number;
  total_trial_rate_events?: number;
  expected_duration?: number;
  data_collection_method?: TPGoalDataCollectionMethod;
  measurement_units_duration_unit?: string | null;
  measurement_units_interval_unit?: string | null;
  target_id: number;
  utc_offset?: number;
}

export interface IClientAssessment {
  bay?: string | null;
  date_start: string;
  utc_offset_date_start: number;
  client_id: number;
  client: IClient;
  duration: number;
  activity_conducted: IAssessmentActivity;
  notes: string;
  id?: number;
  status: AssessmentNoteStatus;
  signature: string;
  utc_offset_date_signed: number;
  date_signed: string;
  assessment_type?: string;
  service_code: string;
  provider: IProvider;
  location: string;
  delivery_method: string;
}

export interface IInvoiceDetails {
  id: number;
  biller: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
  };
  amount_due: number;
  client: IClient;
  discount: number | null;
  billing_facility: string;
  date_due: string;
  date_generated: string;
  date_sent?: string;
  utc_offset_date_generated: number;
  payee: IProvider;
  status: InvoiceStatus;
  claims: IClaimMinified[];
  payments: IInvoicePayment[];
  checkout_session_map: IClaimCheckOutSession[];
  is_any_claim_paid_to_subscriber: boolean;
}

export interface IInvoicePayment {
  amount: number | null;
  mode: InvoicePaymentTypeEnum | null;
  date_received: string | null;
  date_cleared: string | null;
  status: InvoicePaymentStatusEnum | null;
  transaction_id: string | null;
  id: number | null;
}

export interface IClaimMinified {
  adjustments: number;
  allowable: number;
  amount_insurance_paid: number;
  amount_paid_to_subscriber: number;
  balance: number;
  billed: number;
  claim_id: number;
  co_insurance: number;
  co_pay: number;
  deductible: number;
  description: string;
  earliest_date_of_service: string;
  invoice_id: number;
  latest_date_of_service: string;
  patient_responsibility: number;
  units: number;
  is_amount_paid_to_subscriber: boolean;
}

export interface IClaimInvoiceHistory {
  earliest_date_of_service: string;
  id: number;
  status: ClaimStatusType;
  is_invoiceable: boolean;
  invoice_id: number | null;
  latest_date_of_service: string;
  patient_responsibility: number;
  service_code: IServiceCode;
}

export interface IAssessmentActivity {
  is_assessment_administered: boolean;
  is_findings_of_assessment_discussed: boolean;
  is_recommendations_based_on_assessment_discussed: boolean;
  is_past_data_analyzed_non_face_to_face: boolean;
  is_assessment_scored_or_interpreted: boolean;
  is_report_or_treatment_plan_prepared: boolean;
}

export interface DocumentAggregation {
  client_id: number;
  duration: number;
  count: number;
}

export interface IClientSupervision {
  id?: number;
  bay?: string | null;
  utc_offset?: number;
  utc_offset_date_start?: number;
  client_id: number | '';
  client?: IClient;
  date_start: Date | null;
  duration: number | '';
  location: string;
  setting: string;
  participants_names?: string;
  participants?: {
    parent: boolean;
    sibling: boolean;
  };
  clinical_status?: string;
  response_to_treatment?: string;
  session_narrative?: string;
  last_ai_generated_session_narrative?: string;
  overall_progress_toward_mastery?: string;
  modified_behaviour_intervention?: string;
  assessed_modification?: string;
  probed_new_skills?: string;
  reviewed_and_analyzed_data?: string;
  reviewed_and_evaluated_session_notes?: string;
  reviewed_and_evaluated_treatment_plan?: string;
  evaluated_progress?: string;
  parent_demonstrated_new_or_changed_skills?: string;
  target_phase?: ITargetPhase[];
  status?: SupervisionNoteStatus;
  active_treatment_id?: number;
  bt_participants?: {
    primary: IProvider | null;
    secondary: IProvider[];
  };
  bcba?: IProvider;
}

export type CheckedTherapyFields =
  | 'modified_behaviour_intervention'
  | 'assessed_modification'
  | 'probed_new_skills'
  | 'reviewed_and_analyzed_data'
  | 'reviewed_and_evaluated_session_notes'
  | 'reviewed_and_evaluated_treatment_plan'
  | 'evaluated_progress'
  | 'parent_demonstrated_new_or_changed_skills';

export interface ITargetPhase {
  id?: number;
  title: string;
  target_id?: number | '';
  date_added?: string;
  target_name?: string;
  goal_name?: string;
}

export interface IProviderAssignment {
  id?: number;
  client: IClient;
  provider: IProvider;
  clinical_relationship: ClinicalRelationships | null;
  date_start?: string | null;
  date_end?: string | null;
}

export interface IClinicalQAReview {
  id?: number;
  is_datetime_accurate: boolean;
  is_data_collected_per_forta_protocols: boolean;
  is_language_objective: boolean;
  is_note_submitted_within_48hours_of_session: boolean;
  has_10data_points_per_hour: boolean;
  is_at_least_2programs_run_per_hour: boolean;
  is_all_active_programs_used_within_7days: boolean;
  is_detail_sufficient_quantity: boolean;
  is_detail_sufficient_quality: boolean;
  one_setting_per_session_note: boolean;
  therapist_role_aligns_with_tp_goals: boolean;
  is_overall_coherent: boolean;
  date_start: Moment | null;
  eval_schema_version: SessionEvalSchemaVersion;
  utc_offset_date_start: number;
  score: number;
  verdict: Verdict;
  status: CQAReviewStatus;
  reviewing_provider: IProvider;
  date_reviewed: Moment | null;
  utc_offset_date_reviewed: number;
  session_id: number;
  acknowledged_signature: string;
  date_review_acknowledged: Moment;
  utc_offset_date_review_acknowledged: number;
  discourses: IClinicalQAReviewDiscourse[];
}

interface IClinicalQAReviewMinified {
  id?: number;
  date_start: Moment | null;
  utc_offset_date_start: number;
  verdict: Verdict;
  status: CQAReviewStatus;
  date_reviewed: Moment | null;
  utc_offset_date_reviewed: number;
  date_review_acknowledged: Moment | null;
  utc_offset_date_review_acknowledged: number | null;
  score?: number;
  has_submission_discourse_comments?: boolean;
}

export interface IClinicalQAReviewDiscourse {
  id?: number;
  is_edit_requested: boolean;
  comment: string | null;
  date_submitted: Moment | null;
  utc_offset_date_submitted: number | null;
}

export interface ISessionReviewSummary {
  review_pending_duration_in_days: number;
  num_failed_reviews: number;
  provider: IProvider;
}

export interface ISessionWithClaimStatus {
  selected: boolean;
  id: string;
  date: Moment | string;
  provider_name: string;
  status: SessionClaimStatus | ClaimStatusType;
  readonly?: boolean;
  location?: string;
  provider_id?: number;
  claim_history?: IClaimStatusHistory[];
  duration?: number;
  timeZone: string;
  bay?: string;
  units?: number;
  licensed?: boolean;
  credentialed?: boolean;
  clinical_relationship?: ClinicalRelationships;
  author_id?: number;
  isUnderClinicalReview?: boolean;
  session_provider?: string;
  sessionStatus?: SupervisionNoteStatus | BTSessionStatus;
  procedureCode?: ServiceCodes;
  isAwaitingFinalization?: boolean;
}

export interface ISupervisionWithClaimStatus {
  id: string;
  bay?: Bays;
  family_attendance_bay?: Bays;
  client_id: number;
  date_start: Date;
  location: string;
  utc_offset_date_start: number;
  expected_duration: number;
  duration?: number;
  client: IClient;
  author_id?: number;
  status: string;
  claim_history?: IClaimStatusHistory[];
  bcba: IProvider;
  claim_id: number;
}

interface IClaimStatusHistory {
  clearing_house_claim_id: string | null;
  id: number;
  status: ClaimStatusType;
  total_charge: number;
}

export interface IAssessmentWithClaimStatus {
  id: string;
  bay?: Bays;
  client_id: number;
  date_start: Date;
  utc_offset_date_start: number;
  duration: number;
  location: string;
  client: IClient;
  status: string;
  claim_history?: IClaimStatusHistory[];
  provider: IProvider;
  claim_id: number;
}

interface ILinkedClaim {
  clearing_house_claim_id: string | null;
  id?: number;
  status: ClaimStatusType;
  secondary_status: ClaimStatusType;
  total_charge: number;
}

export interface BulkUnbillableSummary {
  claims_created: number;
  procedures_attached: number;
  rows_skipped: number;
  rows_failed: number;
  failures_csv: string;
}

export interface BulkMarkNotesUnbillablePayload {
  file: File;
  getAccessTokenSilently: GetAccessTokenSilently;
}

export interface AdminUpdateClaimStatusData {
  status?: string;
  payment_status?: string;
  reason?: string;
  date_rejected?: string | null;
  reason_for_rejection?: string | null;
}

export interface AdminUpdateClaimStatusPayload {
  claimId: number;
  data: AdminUpdateClaimStatusData;
  getAccessTokenSilently: GetAccessTokenSilently;
}

export interface IClaim {
  allowable: number;
  ancestors: ILinkedClaim[];
  bay: string;
  billed: number;
  balance: number;
  is_session_author_bcba: boolean;
  date_primary_approved: string;
  date_secondary_approved: string;
  date_submitted_to_clearinghouse: string;
  primary_insurance_balance: number;
  secondary_insurance_balance: number;
  payor_claim_id: string;
  payor_appeal_tracking_id: string;
  utc_offset_date_submitted_to_clearinghouse: number;
  utc_offset_date_secondary_submitted_to_clearinghouse: number;
  clearing_house_claim_id: string;
  reason_for_rejection: string;
  date_rejected: string;
  secondary_status: string;
  secondary_reason_for_rejection: string;
  date_secondary_payor_rejected: string;
  secondary_payor_claim_id: string;
  date_secondary_submitted_to_clearinghouse: string;
  amount_recouped: number;
  date_amount_recouped: string;
  recoupment_probability: RecoupmentProbabilityType | null;
  recoupment_reason: RecoupmentReasonType | null;
  descendants: ILinkedClaim[];
  earliest_date_of_service?: string;
  amount_insurance_paid: number;
  co_insurance: number;
  co_pay: number;
  deductible: number;
  adjustments: number;
  patient_responsibility: number;
  date_deposit_cleared_in_bank: string;
  date_patient_paid: string;
  amount_patient_paid: number;
  is_payment_expected: string;
  source: ClaimSourceEnum;
  submission_method: string;
  date_finalized: string;
  id?: number;
  client: IClient;
  bcba: IProvider;
  service_code: IServiceCode;
  biller: IProvider;
  status: ClaimStatusType;
  num_procedures: number;
  procedures: IProcedure[];
  service_lines_date_range: {
    start: Date;
    end: Date;
  };
  policies: IClaimPolicy[];
  service_lines: IServiceLine[];
  date_generated: Date;
  amount_paid_to_subscriber: number;
  utc_offset_date_generated: number;
  form_1500_data: IClaimForm1500Data;
  claim_follow_up_notes: IClaimFollowUpNote[];
  payment_status: ClaimPaymentStatus;
  units: number;
  is_invoiceable: boolean;
  date_last_certified: string;
  utc_offset_date_last_certified: number;
  last_certified_by: {
    id: number;
    first_name: string;
    last_name: string;
    email: string;
  };
  eob_documents: Array<IEobDocument>;
  invoice_id: number;
}

export interface IClaimPolicy {
  id: number;
  payor_responsibility: string;
  payor: {
    label: string;
    state: string;
    type: string;
    id: number;
  };
  billing_payor: {
    label: string;
    state: string;
    type: string;
    id: number;
  };
}

export interface IServiceLine {
  id: number;
  units: number;
  billed: number;
  deductibles: number;
  co_insurance: number;
  co_pay: number;
  amount: number;
  allowable: number;
  adjustments: number;
  service_date: string;
  location: string | null;
  modifier: string[] | null;
  charges_per_unit: number;
  payments: IClaimPayment[];
}

export interface IClaimPayment {
  policy_id: number | null;
  deductibles: number | null;
  co_insurance: number | null;
  co_pay: number | null;
  amount: number | null;
  allowable: number | null;
  adjustments: number | null;
  date_received: string | null;
  date_cleared: string | null;
  mode: PaymentTypeEnum | null;
  transaction_id: string | null;
  is_amount_paid_to_subscriber: boolean;
  id?: number;
  claim_service_line_id?: number;
  policy?: IPaymentPolicy;
  rowIndex?: number;
}

interface IPaymentPolicy {
  id: number;
  payor_responsibility: string;
  payor: {
    id: number;
    label: string;
    state: string;
    type: string;
  };
  billing_payor: {
    label: string;
    state: string;
    type: string;
    id: number;
  };
}

export interface IClaimFollowUpNote {
  follow_up_notes: string;
  date_written: string;
  utc_offset_date_written: number;
  author: IProvider;
  id: number;
  claim_id: number;
}

export interface IClaimForm1500Data {
  qc: string;
  insured: {
    street_address_line_one: string;
    street_address_line_two: string;
    street_address_city: string;
    street_address_state: string;
    street_address_zip: string;
    dob: string;
    sex: string;
    name: string;
    phone: string;
  };
  patient: {
    street_address_line_one: string;
    street_address_line_two: string;
    street_address_city: string;
    street_address_state: string;
    street_address_zip: string;
    phone: string;
    dob: string;
    sex: string;
    name: string;
    account_number: string;
    relationship_to_insured: RelationshipToPolicyHolder;
  };
  payor_id: string;
  total_charge: number;
  other_insured: {
    name: string;
    insurance_plan_name: string;
    member_id: string;
  };
  service_lines: IClaimServiceLine[];
  federal_tax_id: string;
  insurance_type: PayorTypes;
  accept_assignment: string;
  resubmission_code: string;
  billing_provider_info: {
    npi: string;
    zip: string;
    city: string;
    state: string;
    organization: string;
    address_line_one: string;
    address_line_two: string;
  };
  additional_information: string;
  service_facility_address: {
    street_address_line_one: string;
    street_address_line_two: string;
    street_address_city: string;
    street_address_state: string;
    street_address_zip: string;
    npi: string;
    name: string;
  };
  prior_authorization_number: string;
  related_patient_conditions: {
    employment: string;
    auto_accident: string;
    other_accident: string;
  };
  claim_and_service_line_details: {
    rendering_provider: {
      npi: string;
      taxonomy: string;
      first_name: string;
      middle_name: string;
      last_or_organization_name: string;
    };
    pay_to_provider_address: {
      zip: string;
      city: string;
      state: string;
      address_line_one: string;
      address_line_two: string;
    };
    payor_information: {
      primary_id: string;
    };
  };
  is_there_another_health_benefit_plan: boolean;
  diagnosis_or_nature_of_illness_or_injury_related_al_service_lines: string;
  member_id: string;
}

export interface IClaimServiceLine {
  units: number;
  charges: number;
  id_qual: string;
  bcba_npi: string;
  cpt_or_hcpcs_code: string;
  modifier: string[] | null;
  location: number;
  date_of_service: string;
  charges_per_unit: string;
  diagnosis_pointer: string;
}
export interface IProcedure {
  id: number;
  procedure_id: number;
  location: Locations;
  date_start: Date;
  utc_offset_date_start: number;
  duration: number;
}

export interface IPayorContract {
  id: number | null;
  service_code_id: number | null;
  rate_per_unit: number | null;
  date_start: Moment | string | null;
  date_end: Moment | string | null;
}

export interface IClientModifierMatrixRow {
  id: number | null;
  client_id?: number;
  payor_id: number | null;
  service_code_id: number | null;
  service_code?: string;
  location: string | null;
  delivery_method: string | null;
  modifier_1: string | null;
  modifier_2: string | null;
  modifier_3: string | null;
  modifier_4: string | null;
}

export interface IFetchClientModifierMatrixArg {
  clientId: string | undefined;
  getAccessTokenSilently: GetAccessTokenSilently;
}
export interface ICreateClientModifierMatrixRowArg {
  data: Omit<IClientModifierMatrixRow, 'id'>;
  clientId: string | undefined;
  getAccessTokenSilently: GetAccessTokenSilently;
}
export interface IUpdateClientModifierMatrixRowArg {
  data: Omit<IClientModifierMatrixRow, 'id'>;
  clientId: string | undefined;
  rowId: number;
  getAccessTokenSilently: GetAccessTokenSilently;
}
export interface IDeleteClientModifierMatrixRowArg {
  clientId: string | undefined;
  rowId: number | null;
  getAccessTokenSilently: GetAccessTokenSilently;
}

export interface IPayorProviderCredential {
  id: number | null;
  payor: IPayor | null;
  provider: IProvider | null;
  date_start: Moment | string | null;
  date_end: Moment | string | null;
}

export interface IClientAssignment {
  id?: number;
  date_start: string;
  date_end: string | null;
  client: IClient;
  cohort?: ICohort;
}

export interface ICohort {
  id?: number;
  name: string;
  service_classes: Array<string>;
  client_assignments: Array<IClientAssignment>;
}

export interface IClaimInvoice {
  id: number;
  client: IClient;
  payee: IProvider;
  biller: IProvider;
  status: InvoiceStatus;
  amount_billed: number;
  amount_due: number;
  date_sent: Moment;
  date_due: Moment;
}

export interface IClaimInvoiceList {
  total_amount_due: number;
  invoices: IClaimInvoice[];
}

interface IClaimCheckOutSession {
  payment_status: InvoicePaymentStatus;
  payment_id: number;
}

interface IEobDocument {
  id: number;
  claim_id: number;
  file_name: string;
  policy: IClaimPolicy;
  document_url: string;
}

export type ValueOf<T> = T[keyof T];
export type Bays = ValueOf<typeof BAYS>;
export type InsuranceType = ValueOf<typeof INSURANCE_TYPES>;

export interface IEmailCronConfig {
  id: number;
  job_key: string;
  enabled: boolean;
  subject: string | null;
  html_body: string | null;
  recipients: string[] | null;
  error_notification_recipient: string | null;
  days_of_week: string[] | null;
  updated_by: string | null;
  is_runnable: boolean;
}

export interface IEmailCronConfigPreviewResponse {
  subject_rendered: string;
  html_rendered: string;
  recipients_resolved: string[];
  has_data: boolean;
}

// Auth0's token getter, threaded through the email-cron thunks.
export type GetAccessTokenSilently = Auth0ContextInterface['getAccessTokenSilently'];

// Editable fields for create/update (toggle sends just `enabled`).
export interface IEmailCronConfigPayload {
  job_key?: string;
  enabled?: boolean;
  subject?: string | null;
  recipients?: string[] | null;
  error_notification_recipient?: string | null;
  html_body?: string | null;
  days_of_week?: string[] | null;
}

export interface IEmailCronConfigPreviewPayload {
  subject: string | null;
  html_body: string | null;
}

// email-cron slice state + thunk argument payloads (kept out of the slice file).
export interface IEmailCronConfigState {
  list: IEmailCronConfig[];
  current: IEmailCronConfig | null;
  // Most recently requested config id; used to discard late getById responses.
  requestedConfigId: string | null;
  preview: IEmailCronConfigPreviewResponse | null;
  // The config a stored preview belongs to; prevents a late preview from
  // rendering its (PHI-bearing) HTML under a different config.
  previewConfigId: string | null;
  loading: boolean;
}

export interface IEmailCronAuthArg {
  getAccessTokenSilently: GetAccessTokenSilently;
}
export interface IEmailCronIdArg extends IEmailCronAuthArg {
  id: string | number;
}
export interface IEmailCronCreateArg extends IEmailCronAuthArg {
  data: IEmailCronConfigPayload;
}
export interface IEmailCronUpdateArg extends IEmailCronIdArg {
  data: IEmailCronConfigPayload;
}
export interface IEmailCronPreviewArg extends IEmailCronIdArg {
  data: IEmailCronConfigPreviewPayload;
}
